import AddMealPlanRecipesToPlanner from "@/components/MealPlans/AddMealPlanRecipesToPlanner";
import cloudflareLoader from "@/lib/cloudflare-images";
import { Asset } from "@/types/asset";
import { Recipe } from "@/types/recipe";
import clsx from "clsx";
import Image from "next/legacy/image";
import Link from "next/link";
import React, { FunctionComponent, useEffect, useState } from "react";
import { Autoplay, EffectFade } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

interface MealPlanHeroProps {
  mealPlan?: any;
  recipe?: Recipe;
  eyebrow?: string;
  eyebrowHref?: string;
  logo?: React.ReactNode;
  heading?: string;
  introduction: string | React.ReactNode;
  images?: Array<Asset>;
  showActions?: boolean;
  nextRecipeHref?: string | null;
  prevRecipeHref?: string | null;
  restricted?: boolean;
  showImageOnMobile?: boolean;
}

const MealPlanHero: FunctionComponent<MealPlanHeroProps> = ({
  mealPlan = null,
  recipe,
  eyebrow = "",
  eyebrowHref = "",
  logo = null,
  heading = "",
  introduction,
  images = [],
  showActions = true,
  nextRecipeHref = null,
  prevRecipeHref = null,
  restricted = false,
  showImageOnMobile = false,
}) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, [setIsMounted]);

  return (
    (<div className={clsx("MealPlanHero", "")}>
      <div
        className={clsx(
          "MealPlanHero__panels",
          "flex flex-col text-current xl:flex-row-reverse"
        )}
      >
        <div
          className={clsx("MealPlanHero__imagePanel", "w-full md:w-1/2", {
            block: showImageOnMobile,
            "hidden md:block": !showImageOnMobile,
          })}
        >
          <div className={clsx("relative aspect-square")}>
            {images.length === 1 && (
              <Image
                src={images[0]?.url}
                alt={images[0]?.title}
                layout="fill"
                objectFit="cover"
                loader={cloudflareLoader}
              />
            )}
            {images.length > 1 && (
              <Swiper
                loop
                className={clsx(
                  "aspect-square md:!absolute md:!inset-0 md:aspect-auto"
                )}
                modules={[EffectFade, Autoplay]}
                effect="fade"
                speed={100}
                autoplay={{
                  delay: 750,
                }}
              >
                {images.map((image) => {
                  return (
                    <SwiperSlide key={image.id}>
                      <Image
                        src={image.url}
                        alt={image.title}
                        layout="fill"
                        objectFit="cover"
                        loader={cloudflareLoader}
                      />
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            )}
          </div>
        </div>
        <div
          className={clsx(
            "MealPlanHero__textPanel",
            "relative flex w-full flex-col justify-end p-6 md:w-1/2 md:p-12"
          )}
        >
          <div className={clsx("MealPlanHero__content", "")}>
            <div className={clsx("space-y-4 lg:space-y-6")}>
              {!!logo && (
                <div
                  className={clsx(
                    "MealPlanHero__logo",
                    "h-32 2xl:h-40 [&_svg]:h-full"
                  )}
                >
                  {logo}
                </div>
              )}
              {!!eyebrow && (
                <Link
                  href={eyebrowHref}
                  className={clsx(
                    "MealPlanHero__eyebrow",
                    "group relative font-body text-sm hover:opacity-75"
                  )}>

                  {eyebrow}

                </Link>
              )}
              {!!heading && (
                <h1
                  className={clsx("MealPlanHero__heading", "heading-lg--thin")}
                >
                  {heading}
                </h1>
              )}
              <div
                className={clsx(
                  "MealPlanHero__introduction",
                  "body-text-sm relative max-w-prose"
                )}
                dangerouslySetInnerHTML={{ __html: introduction }}
              ></div>
              {isMounted && !!mealPlan && (
                <div>
                  <_AddToPlanner mealPlan={mealPlan} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>)
  );
};

const _NavButton = ({ href, label, icon }) => {
  const Icon = icon;

  return (
    (<Link
      href={href}
      className={clsx(
        "flex h-12 items-center justify-center rounded-full border-2 border-white bg-white px-6 text-base text-zinc-800 hover:bg-zinc-50 hover:text-zinc-700 focus:border-white max-lg:w-12"
      )}>

      <span className={clsx("hidden whitespace-nowrap lg:block")}>
        {label}
      </span>
      <span className={clsx("block lg:hidden")}>
        <Icon className={clsx("h-6 w-6 stroke-2")} />
      </span>

    </Link>)
  );
};

const _AddToPlanner = ({ mealPlan }) => {
  const variant = mealPlan.planVariants?.[0];

  // If the meal plan is legacy with a variant, use the variant's recipes, otherwise use the meal plan's recipes
  let recipeIds = [];

  if (!!variant) {
    recipeIds = variant?.recipes.map((recipe) => recipe.id);
  } else {
    recipeIds = mealPlan.recipes.map((recipe) => recipe.id);

    // The meal plan might have a base recipe too
    if (!!mealPlan.baseRecipe?.[0]) {
      recipeIds.push(mealPlan.baseRecipe[0].id);
    }
  }

  return (
    <AddMealPlanRecipesToPlanner
      mealPlanId={mealPlan.id}
      recipeIds={recipeIds}
      plannerTitle={mealPlan.title}
    />
  );
};

export default MealPlanHero;
